/**
 * Converts a time string in the format "HH:MM:SS" to milliseconds.
 *
 * @param {string} timeString - The time string to convert, in the format "HH:MM:SS".
 * @returns {number} The total number of milliseconds represented by the time string.
 * @throws {Error} If the time string is invalid.
 */
export const timeStringToMilliseconds = (timeString?: string | null) => {
  try {
    if (!timeString) {
      return null;
    }
    const [hours, minutes, seconds] = timeString.split(':').map(Number);

    const totalMilliseconds =
      hours * 3600 * 1000 + // Convert hours to milliseconds
      minutes * 60 * 1000 + // Convert minutes to milliseconds
      seconds * 1000; // Convert seconds to milliseconds

    return totalMilliseconds;
  } catch (_error) {
    throw new Error('Invalid time string');
  }
};

/**
 * @returns The number of milliseconds in the passed number of days.
 */
export const daysToMilliseconds = (days: number) => {
  return days * 24 * 60 * 60 * 1000;
};

/**
 * Compares two ISO-8601 timestamps and determines ordering.
 * Returns -1 if a < b, 1 if a > b, and 0 if equal.
 */
export const compareIsoTimestamps = (a: string, b: string): -1 | 0 | 1 => {
  const aTime = Date.parse(a);
  const bTime = Date.parse(b);
  if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
    throw new Error('Invalid ISO timestamp');
  }
  if (aTime < bTime) return -1;
  if (aTime > bTime) return 1;
  return 0;
};

/**
 * Returns true if timestamp a is strictly before timestamp b.
 */
export const isIsoBefore = (a: string, b: string): boolean => {
  return compareIsoTimestamps(a, b) === -1;
};

/**
 * Converts a TimeSpan-formatted string (`HH:MM:SS`) to total seconds.
 * Returns 0 for an empty/missing input so callers can use the result
 * directly in numeric comparisons without null checks.
 */
export const convertTimeSpanToSeconds = (timeSpan?: string): number => {
  if (!timeSpan) {
    return 0;
  }

  const [hours, minutes, seconds] = timeSpan.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Converts a number of seconds into a zero-padded TimeSpan string
 * (`HH:MM:SS`).
 */
export const convertSecondsToTimeSpan = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(remainingSeconds).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};
